import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Collapse,
  Empty,
  Input,
  Pagination,
  Select,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const { Title } = Typography;

const PAGE_SIZE = 12;

const STATUS_BADGE_CLASS = {
  On: 'bg-red-50 text-red-700 border-red-200',
  Off: 'bg-gray-100 text-gray-600 border-gray-200',
  Available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Fault: 'bg-red-50 text-red-700 border-red-200',
  'N/A': 'bg-gray-100 text-gray-500 border-gray-200',
};

const STATUS_I18N_KEY = {
  On: 'statusOn',
  Off: 'statusOff',
  Available: 'statusAvailable',
  Fault: 'statusFault',
  'N/A': 'statusNA',
};

const localized = (item, field, lang) => {
  const suffix = lang?.startsWith('tr') ? 'Tr' : 'En';
  return item[`${field}${suffix}`] ?? item[`${field}En`] ?? '';
};

const StatusBadge = ({ value, t }) => {
  const label = t(STATUS_I18N_KEY[value] || 'statusNA');
  const className = STATUS_BADGE_CLASS[value] || STATUS_BADGE_CLASS['N/A'];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
};

const DiagnosticCard = ({ item, t, lang }) => {
  const title = localized(item, 'title', lang);
  const description = localized(item, 'description', lang);
  const solution = localized(item, 'solution', lang);
  const remarks = localized(item, 'remarks', lang);

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start gap-3">
        <span className="shrink-0 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
          {item.errorCode}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900 leading-snug">
            {title}
          </div>
          <div className="mt-0.5 text-xs text-gray-500">{item.vendor}</div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
        <span>{t('specificFault')}:</span>
        <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
          {item.specificFault}
        </span>
      </div>

      <div className="mb-3">
        <div className="mb-1 text-xs font-semibold text-gray-700">
          {t('description')}
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>

      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">{t('redLed')}:</span>
          <StatusBadge value={item.redLed} t={t} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">{t('chargerCp')}:</span>
          <StatusBadge value={item.chargerCp} t={t} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">{t('gunCp')}:</span>
          <StatusBadge value={item.gunCp} t={t} />
        </div>
      </div>

      <div className="mt-auto">
        <Collapse
          ghost
          size="small"
          items={[
            {
              key: 'solution',
              label: (
                <span className="text-sm font-medium text-gray-800">
                  {t('solution')}
                </span>
              ),
              children: (
                <p className="whitespace-pre-wrap text-sm text-gray-600 leading-relaxed">
                  {solution || t('notAvailable')}
                </p>
              ),
            },
          ]}
        />

        {remarks ? (
          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <div className="mb-1 text-xs font-semibold text-amber-800">
              {t('remarks')}
            </div>
            <p className="text-sm text-amber-900 leading-relaxed">{remarks}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const DiagnosticPage = () => {
  const { t, i18n } = useTranslation('diagnostic');
  const location = useLocation();
  const initialErrorCode = useMemo(() => {
    return new URLSearchParams(location.search).get('ErrorCode') || '';
  }, [location.search]);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState(initialErrorCode);
  const [vendor, setVendor] = useState(null);
  const [specificFault, setSpecificFault] = useState(null);
  const [redLed, setRedLed] = useState(null);
  const [chargerCp, setChargerCp] = useState(null);
  const [gunCp, setGunCp] = useState(null);
  const [page, setPage] = useState(1);

  const loadData = useCallback(() => {
    setLoading(true);
    fetch('/diagnosticData.json')
      .then((response) => response.json())
      .then((json) => {
        setData(Array.isArray(json) ? json : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error(t('errorOccurred'), error);
        setData([]);
        setLoading(false);
      });
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (initialErrorCode) {
      setSearchValue(initialErrorCode);
      setPage(1);
    }
  }, [initialErrorCode]);

  const vendorOptions = useMemo(
    () =>
      Array.from(new Set(data.map((item) => item.vendor)))
        .sort()
        .map((value) => ({ label: value, value })),
    [data]
  );

  const faultOptions = useMemo(
    () =>
      Array.from(new Set(data.map((item) => item.specificFault)))
        .sort()
        .map((value) => ({ label: value, value })),
    [data]
  );

  const statusOptions = useMemo(
    () =>
      ['On', 'Off', 'Available', 'Fault', 'N/A'].map((value) => ({
        label: t(STATUS_I18N_KEY[value]),
        value,
      })),
    [t]
  );

  const filteredData = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const lang = i18n.language;

    return data.filter((item) => {
      if (vendor && item.vendor !== vendor) return false;
      if (specificFault && item.specificFault !== specificFault) return false;
      if (redLed && item.redLed !== redLed) return false;
      if (chargerCp && item.chargerCp !== chargerCp) return false;
      if (gunCp && item.gunCp !== gunCp) return false;

      if (!query) return true;

      const haystack = [
        item.errorCode,
        item.vendor,
        item.specificFault,
        localized(item, 'title', lang),
        localized(item, 'description', lang),
        localized(item, 'remarks', lang),
        item.descriptionEn,
        item.descriptionTr,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [
    data,
    searchValue,
    vendor,
    specificFault,
    redLed,
    chargerCp,
    gunCp,
    i18n.language,
  ]);

  useEffect(() => {
    setPage(1);
  }, [searchValue, vendor, specificFault, redLed, chargerCp, gunCp]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredData.slice(start, start + PAGE_SIZE);
  }, [filteredData, page]);

  const handleRefresh = () => {
    setSearchValue(initialErrorCode || '');
    setVendor(null);
    setSpecificFault(null);
    setRedLed(null);
    setChargerCp(null);
    setGunCp(null);
    setPage(1);
    loadData();
  };

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Spin size="small" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-gray-400" />}
          placeholder={t('searchPlaceholder')}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="w-full lg:max-w-xs"
        />
        <Select
          allowClear
          placeholder={t('vendor')}
          options={vendorOptions}
          value={vendor}
          onChange={setVendor}
          className="w-full lg:w-40"
        />
        <Select
          allowClear
          placeholder={t('specificFault')}
          options={faultOptions}
          value={specificFault}
          onChange={setSpecificFault}
          className="w-full lg:w-44"
        />
        <Select
          allowClear
          placeholder={t('redLed')}
          options={statusOptions.filter((o) =>
            ['On', 'Off', 'N/A'].includes(o.value)
          )}
          value={redLed}
          onChange={setRedLed}
          className="w-full lg:w-36"
        />
        <Select
          allowClear
          placeholder={t('chargerCp')}
          options={statusOptions.filter((o) =>
            ['Available', 'Fault', 'N/A'].includes(o.value)
          )}
          value={chargerCp}
          onChange={setChargerCp}
          className="w-full lg:w-40"
        />
        <Select
          allowClear
          placeholder={t('gunCp')}
          options={statusOptions.filter((o) =>
            ['Available', 'Fault', 'N/A'].includes(o.value)
          )}
          value={gunCp}
          onChange={setGunCp}
          className="w-full lg:w-36"
        />
        <Tooltip title={t('refresh')}>
          <button
            type="button"
            onClick={handleRefresh}
            aria-label={t('refresh')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
          >
            <ReloadOutlined />
          </button>
        </Tooltip>
      </div>

      <div className="text-sm text-gray-600">
        {t('resultsCount', { count: filteredData.length })}
      </div>

      {filteredData.length === 0 ? (
        <Empty description={t('noResults')} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {pagedData.map((item) => (
              <DiagnosticCard
                key={item.id}
                item={item}
                t={t}
                lang={i18n.language}
              />
            ))}
          </div>

          {filteredData.length > PAGE_SIZE ? (
            <div className="flex justify-center pt-2">
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={filteredData.length}
                onChange={setPage}
                showSizeChanger={false}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default DiagnosticPage;
